import { useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Button,
  Dropdown,
  DropdownContent,
  Icon,
  Label,
  showToast,
  SpinnerLoader,
  TextFieldWithLabel,
} from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  type IAICallingType,
  type ICallingListResponse,
  type IResponseFullAICallingAgent,
  type IResponseTemplate,
} from 'entities/AICalling/api/types'
import { AICallingAgentManageContext } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/context'
import { AICallingAgentManageStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/store/AICallingAgentManageStore'
import { Greeting } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Greeting/Greeting'
import { EscalateToHuman } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/EscalateToHuman/EscalateToHuman'
import { AIKnowledgeBase } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AIKnowledgeBase/AIKnowledgeBase'
import { AgentInstructions } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/AgentInstructions'
import { TestAgent } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/TestAgent/TestAgent'
import { useGetAgentCallingTemplates } from 'pages/chatbot/pages/calling/hooks/useGetAgentCallingTemplates'
import { CallingTemplateMap, CallingTemplates } from 'pages/chatbot/config'
import styles from './styles.module.scss'

type ICreateModalContentProps = {
  type: IAICallingType
  modalId: string
  onCreated: (agent: ICallingListResponse) => void
}

type ICreateModalFormProps = {
  type: IAICallingType
  modalId: string
  templates: IResponseTemplate[]
  onCreated: (agent: ICallingListResponse) => void
}

const CreateModalForm = observer(
  ({ type, modalId, templates, onCreated }: ICreateModalFormProps) => {
    const entities = useEntitiesContext()
    const triggerRef = useRef<HTMLLabelElement | null>(null)
    const [store] = useState(
      () =>
        new AICallingAgentManageStore(entities, {
          templates,
          type,
          initialStatus: 'active',
          onSuccessSaved: async (
            data: IResponseFullAICallingAgent,
            options?: { isForTest?: boolean }
          ) => {
            if (!options?.isForTest) {
              modalStore.closeModal(modalId)
            }
            onCreated(data)
          },
        })
    )

    const handleShowToast = () => {
      showToast({
        type: 'error',
        title: 'Failed to create agent',
        desc: 'Complete all required fields',
      })
    }

    const handleCreate = async () => {
      if (!store.checkValidateForCreate()) {
        handleShowToast()
        return
      }

      try {
        await store.onSave()
      } catch {
        handleShowToast()
      }
    }

    const handleCancel = () => {
      modalStore.closeModal(modalId)
    }

    const currentTemplate = CallingTemplateMap[store.type]

    return (
      <AICallingAgentManageContext.Provider value={store}>
        <div className={styles.formContent}>
          <div className={styles.field}>
            <Label label={{ text: 'Template' }} />
            <Dropdown
              ariaLabel='TemplateSelect'
              placement='bottom-start'
              triggerComponent={() => (
                <TextFieldPlaceholder
                  ref={triggerRef}
                  variant='stroke'
                  size='medium'
                  rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
                  placeholder={
                    <div className={styles.templateTrigger}>
                      <Icon icon={currentTemplate.icon} color='var(--content-brand-primary)' />
                      <span>{currentTemplate.name}</span>
                    </div>
                  }
                />
              )}
              customComponent={(close) => (
                <DropdownContent noPadding width={triggerRef.current?.offsetWidth}>
                  <div className={styles.templateDropdown}>
                    {CallingTemplates.map((template) => (
                      <div
                        key={template.type}
                        className={styles.templateItem}
                        onClick={() => {
                          store.setType(template.type)
                          close()
                        }}
                      >
                        <div className={styles.templateItemLeft}>
                          <Icon icon={template.icon} color='var(--content-brand-primary)' />
                          <div>
                            <div className={styles.templateItemName}>{template.name}</div>
                            <div className={styles.templateItemDesc}>{template.description}</div>
                          </div>
                        </div>
                        {store.type === template.type && (
                          <span className={styles.templateItemCheck}>
                            <Icon icon='check1' />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </DropdownContent>
              )}
            />
          </div>
          <TextFieldWithLabel
            labelProps={{ label: { text: 'Agent name' } }}
            textFieldProps={{
              value: store.nameInputStore.name,
              onChange: store.nameInputStore.setName,
              onFocus: () => store.nameInputStore.setError(''),
              InputProps: {
                placeholder: 'Enter agent name',
              },
            }}
            errorMessage={store.nameInputStore.error}
            limit={store.nameInputStore.limit}
          />
          <Greeting />
          <EscalateToHuman />
          <AIKnowledgeBase />
          <AgentInstructions store={store.agentInstructionsStore} />
        </div>
        <div className={styles.formActions}>
          <TestAgent />
          <div className={styles.formActionsRight}>
            <Button text='Cancel' size='medium' contained='secondary' onClick={handleCancel} />
            <Button text={store.isEdit ? 'Save' : 'Create'} size='medium' onClick={handleCreate} />
          </div>
        </div>
      </AICallingAgentManageContext.Provider>
    )
  }
)

export const CreateModalContent = ({ type, modalId, onCreated }: ICreateModalContentProps) => {
  const [templates, loading] = useGetAgentCallingTemplates()

  if (loading) {
    return (
      <div className={styles.loader}>
        <SpinnerLoader ariaLabel='CreateModalContent' />
      </div>
    )
  }
  if (!templates) return null

  return (
    <CreateModalForm type={type} modalId={modalId} templates={templates} onCreated={onCreated} />
  )
}
